import {
  Component, ElementRef,
  HostBinding,
  Input, OnChanges,
  OnDestroy,
  OnInit, Optional, Self, SimpleChanges,
  ViewChild,
} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {debounceTime, map, startWith, tap} from 'rxjs/operators';
import {ContactTagLookupModel} from '../../api/models';
import { MatFormFieldControl, MatFormField } from '@angular/material/form-field';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { UntypedFormControl, NgControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {FocusMonitor} from '@angular/cdk/a11y';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import { MatChipInputEvent, MatChipGrid, MatChipRow, MatChipRemove, MatChipInput } from '@angular/material/chips';
import {findPhoneNumbersInText, parse} from 'libphonenumber-js';
import * as _ from 'lodash';
import {PhoneNumberPipe} from '../../core/pipes/phone-number.pipe';
import {MatDialog} from '@angular/material/dialog';
import { MessagingService } from '../messaging.service';
import {MatTooltip} from '@angular/material/tooltip';
import { MatFormFieldFix } from '../../editors/mat-form-field/mat-form-field-fix';
import { NgFor, NgIf, NgStyle } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOptgroup, MatOption } from '@angular/material/core';

@Component({
    selector: 'app-thread-recipients',
    template: `
    <mat-form-field class="thread-recipients-field" appearance="outline">
      <mat-chip-grid #chipList matTooltip="Click to copy">
        <mat-chip-row *ngFor="let item of selectedItems" [removable]="!disabled" (removed)="remove(item)">
          <div class="recipient-title" #tooltip="matTooltip" matTooltip="">
            <span (click)="copyValue(item.name)">{{ item.name }}</span> <span class="messaging-number"
                                                                              (click)="copyValue(item.phoneNumber)">{{ item.phoneNumber | phoneNumber }}</span>
          </div>
          <mat-icon matChipRemove *ngIf="!disabled">cancel</mat-icon>
        </mat-chip-row>

        <input type="text" #inputTrigger [placeholder]="placeholder"
               [ngStyle]="{display: !disabled && this.selectedItems.length < 10 ? 'block' : 'none'}"
               matInput [matAutocomplete]="autoComplete" [formControl]="itemControl"
               [matChipInputFor]="chipList"
               [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
               (matChipInputTokenEnd)="addRecipient($event)"
               (paste)="onPaste($event)" />
      </mat-chip-grid>

      <mat-autocomplete #autoComplete="matAutocomplete" (optionSelected)="optionSelected($event)">
        <mat-optgroup *ngFor="let group of groups" [label]="group">
          <mat-option *ngFor="let item of getGroupItems(group)" [value]="item" [disabled]="item.uniqueKey === 'invalid'">
            {{ item.name }} {{ item.phoneNumber | phoneNumber }}
          </mat-option>
        </mat-optgroup>

      </mat-autocomplete>
    </mat-form-field>
  `,
    styleUrls: ['./thread-recipients.component.scss'],
    providers: [{ provide: MatFormFieldControl, useExisting: ThreadRecipientsComponent }],
    standalone: true,
    imports: [MatFormFieldFix, MatFormField, MatChipGrid, MatTooltip, NgFor, MatChipRow, NgIf, MatIcon, MatChipRemove, MatInput, MatAutocompleteTrigger, FormsModule, MatChipInput, NgStyle, ReactiveFormsModule, MatAutocomplete, MatOptgroup, MatOption, PhoneNumberPipe]
})
export class ThreadRecipientsComponent implements OnInit, OnDestroy, OnChanges, MatFormFieldControl<ContactTagLookupModel> {

  static nextId = 0;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  groups = ['Contacts'];
  phoneNumberPipe = new PhoneNumberPipe();

  @ViewChild('tooltip') tooltip: MatTooltip;

  @Input() set value(value: any) {
    if (value) {
      this.selectedItems = value;
    }
    this.stateChanges.next();
  }

  get value() {
    return this.selectedItems;
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }

  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  selectable = true;
  removable = true;

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private fm: FocusMonitor,
    private elRef: ElementRef<HTMLElement>,
    private messagingService: MessagingService,
    public dialog: MatDialog,
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
    fm.monitor(elRef.nativeElement, true).subscribe(origin => {
      this.focused = this.disabled ? false : !!origin;
      this.stateChanges.next();
    });
  }

  @Input() disabled: boolean;

  readonly autofilled: boolean;
  readonly controlType: string;
  readonly empty: boolean;
  readonly errorState: boolean;
  readonly required: boolean;
  readonly shouldLabelFloat: boolean;
  readonly userAriaDescribedBy: string;

  @ViewChild('inputTrigger') inputTrigger: ElementRef<HTMLInputElement>;
  itemControl = new UntypedFormControl();
  stateChanges = new Subject<void>();
  private _placeholder: string;
  @HostBinding() id = `input-ac-${ThreadRecipientsComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedBy = '';
  private changeCallback: Function;
  private touchedCallback: Function;
  focused = false;
  isAllSelected = false;

  items: ContactTagLookupModel[] = [];
  selectedItems: ContactTagLookupModel[] = [];

  lastFilter = '';

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  writeValue(value: any) {
    if (value) {
      this.selectedItems = value;
    }
    this.stateChanges.next();
  }

  registerOnChange(fn: Function) {
    this.changeCallback = fn;
  }

  registerOnTouched(fn: Function) {
    this.touchedCallback = fn;
  }

  getGroupItems(group: string): ContactTagLookupModel[] {
    return this.items.filter(x => x.type === group);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['disabled']) {
      this.focused = false;
    }
  }

  ngOnInit() {
    this.itemControl.valueChanges.pipe(
      debounceTime(500),
      startWith<string | ContactTagLookupModel[]>(''),
      map(value => typeof value === 'string' ? value : this.lastFilter),
      map(searchText => {
        console.log('filter contacts', searchText);
        return this.filter(searchText)
          .pipe(
            tap((data) => {
              if (!!searchText) {
                const newItemText = this.phoneNumberPipe.transform(searchText, true);
                if (newItemText) {
                  const isValid = !newItemText.includes('invalid');
                  if (isValid) {
                    const phoneNumber = parse(searchText, {defaultCountry: 'US', extended: true});
                    if (phoneNumber.valid) {
                      data.splice(0, 0, {
                        phoneNumber: phoneNumber.phone as string,
                        name: '',
                        type: 'Contacts',
                        uniqueKey: `${phoneNumber.phone || ''}|Contacts||''}`,
                        communicationType: 0,
                        isOptedIn: false
                      });
                    }
                  } else {
                    data.splice(0, 0, {
                      phoneNumber: newItemText,
                      name: '',
                      type: 'Contacts',
                      uniqueKey: 'invalid',
                      communicationType: 0,
                      isOptedIn: false
                    });
                  }
                }
              }
            })
          );
      })
    ).subscribe((data => {
      data.subscribe((items) => this.items = items);
    }));
  }

  remove(item: ContactTagLookupModel): void {
    const index = this.selectedItems.findIndex(value => value.uniqueKey === item.uniqueKey);
    if (index >= 0) {
      this.selectedItems.splice(index, 1);
      this.changeCallback(this.selectedItems);
    }
  }

  clicker() {
    // this.inputTrigger.openPanel();
  }

  filter(searchText: string): Observable<ContactTagLookupModel[]> {
    return this.messagingService.getContactsAndTagsObservable(searchText);
  }

  ngOnDestroy() {
    this.fm.stopMonitoring(this.elRef.nativeElement);
    this.stateChanges.complete();
  }

  onContainerClick(event: MouseEvent): void {
  }

  addRecipient($event: MatChipInputEvent) {
    console.log($event);

    const phoneNumber = parse($event.value, {defaultCountry: 'US', extended: true});
    if (phoneNumber.valid) {
      this.selectedItems.push({
        phoneNumber: phoneNumber.phone as string,
        name: '',
        type: 'Contacts',
        uniqueKey: `${phoneNumber.phone || ''}|Contacts||''}`,
        communicationType: 0,
        isOptedIn: false
      });
      this.changeCallback(this.selectedItems);
      this.clearInput();
    } else {
      return undefined;
    }
  }

  optionSelected($event: MatAutocompleteSelectedEvent) {
    console.log($event);
    const option: ContactTagLookupModel = $event.option.value;
    this.clearInput();

    const i = this.selectedItems.findIndex(value => value.uniqueKey === option.uniqueKey);

    if (i === -1) {
      this.selectedItems.push(option);
      this.changeCallback(this.selectedItems);
    }
  }

  onPaste(event: ClipboardEvent) {
    let text = event.clipboardData.getData('text');
    if (text) {
      // get rid of invalid characters
      text = text.replace(/[#_,;]/g, '|');
      text = text.replace(/\s/g, '');
    }
    const phoneNumbers = findPhoneNumbersInText(text, {defaultCountry: 'US'});

    if (phoneNumbers.length > 0) {
      const pastedPhoneNumbers = phoneNumbers.map(x => {
        return {
          phoneNumber: x.number.number as string,
          name: '',
          type: 'Contacts',
          uniqueKey: `${x.number.number || ''}|Contacts||''}`,
          communicationType: 0,
          isOptedIn: false
        };
      });

      let clientPhoneNumbers = this.selectedItems;

      clientPhoneNumbers = _.concat(clientPhoneNumbers, pastedPhoneNumbers);
      clientPhoneNumbers = _.uniq(clientPhoneNumbers);
      this.selectedItems = clientPhoneNumbers;
      this.changeCallback(this.selectedItems);
    }

    this.clearInput();

    event.preventDefault();

    return false;
  }

  clearInput() {
    this.inputTrigger.nativeElement.value = '';
    this.itemControl.setValue(null);
  }

  copyValue(item: string) {
    navigator.clipboard.writeText(item).then(() => {
      console.log('notify user');
      this.tooltip.message = 'Copied';
      this.tooltip.show();

      setTimeout(() => {
        this.tooltip.message = '';
      }, 1000);
    });
  }
}
